import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { HttpService } from '~/core/services/http.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { WellsApiService } from '~/services/wells-api.service';
import { NotificationService } from '~/services/notification.service';
import { WellViewMappingService } from '~/services/wellview-mapping.service';
import { UnitConversionService } from '~/services/unit-conversion.service';

interface FillCommentsFieldConfig {
    key: string;
    label: string;
    type: string;
    aliases: string[];
    internalFieldName?: string; // Internal field name from wv_mappings.json for unit lookup
}

interface FillCommentsSchemaConfig {
    displayName: string;
    apiName: string;
    primaryKeyField: string;
    fields: FillCommentsFieldConfig[];
}

@Component({
    selector: 'app-fill-comments',
    templateUrl: './fill-comments.component.html',
    styleUrls: ['./fill-comments.component.scss'],
    standalone: false
})
export class FillCommentsComponent implements OnInit {
    @Input() wellName: string = '';
    @Input() processedSchemas: any[] = [];
    @Output() closed = new EventEmitter<void>();
    @Output() confirmedProceed = new EventEmitter<void>();  // Changed from 'saved' for bulk ingest flow

    public loading: boolean = false;
    public saving: boolean = false;
    public schemas: any[] = [];
    public schemaViewToggle: { [schemaIndex: number]: boolean } = {};
    public selectedSchemaApiName: string = '';

    // Cache for schema data by well name
    private static schemasCache: { [wellName: string]: any[] } = {};

    private readonly SCHEMA_CONFIG: FillCommentsSchemaConfig[] = [
        {
            displayName: 'Well Header',
            apiName: 'wellheader',
            primaryKeyField: 'Well Name',
            fields: [
                { key: 'Comments', label: 'Comments', type: 'text', aliases: ['Comments', 'com'], internalFieldName: 'com' }
            ]
        },
        {
            displayName: 'Casing',
            apiName: 'casing',
            primaryKeyField: 'Item Description',
            fields: [
                { key: 'Comments', label: 'Comments', type: 'text', aliases: ['Comments', 'comment'], internalFieldName: 'des' }
            ]
        },
        {
            displayName: 'Casing Components',
            apiName: 'casingcomponents',
            primaryKeyField: 'Component OD',
            fields: [
                { key: 'Component ID', label: 'Component ID', type: 'text', aliases: ['Component ID', 'szidnom'], internalFieldName: 'szidnom' }
            ]
        },
        {
            displayName: 'Cement Stage',
            apiName: 'cementstage',
            primaryKeyField: 'Stage Description',
            fields: [
                { key: 'Comments', label: 'Comments', type: 'text', aliases: ['Comments', 'com'], internalFieldName: 'com' },
                { key: 'Top Depth', label: 'Top Depth', type: 'int', aliases: ['Top Depth', 'depthtop'], internalFieldName: 'depthtop' },
                { key: 'Bottom Depth', label: 'Bottom Depth', type: 'int', aliases: ['Bottom Depth', 'depthbtm'], internalFieldName: 'depthbtm' }
            ]
        },
        {
            displayName: 'Perforation',
            apiName: 'perforation',
            primaryKeyField: 'Proposed/Actual',
            fields: [
                { key: 'Perforation Top Depth', label: 'Perforation Top Depth', type: 'int', aliases: ['Perforation Top Depth', 'depthtop'], internalFieldName: 'depthtop' },
                { key: 'Perforation Bottom Depth', label: 'Perforation Bottom Depth', type: 'int', aliases: ['Perforation Bottom Depth', 'depthbtm'], internalFieldName: 'depthbtm' }
            ]
        },
        {
            displayName: 'Other in Hole',
            apiName: 'otherinhole',
            primaryKeyField: 'Description',
            fields: [
                { key: 'Nominal OD', label: 'Nominal OD', type: 'text', aliases: ['Nominal OD', 'szodnom'], internalFieldName: 'szodnom' },
                { key: 'Top Depth', label: 'Top Depth', type: 'int', aliases: ['Top Depth', 'depthtop'], internalFieldName: 'depthtop' },
                { key: 'Bottom Depth', label: 'Bottom Depth', type: 'int', aliases: ['Bottom Depth', 'depthbtm'], internalFieldName: 'depthbtm' }
            ]
        }
    ];

    constructor(
        private httpService: HttpService,
        private appSettingsService: AppSettingsService,
        private wellsApiService: WellsApiService,
        private notificationService: NotificationService,
        private wellViewMappingService: WellViewMappingService,
        private unitConversionService: UnitConversionService
    ) {}

    ngOnInit(): void {
        this.loadSchemas();
    }

    public async loadSchemas(): Promise<void> {
        this.loading = true;
        this.schemas = [];

        // Check if data is cached for this well
        if (FillCommentsComponent.schemasCache[this.wellName]) {
            this.schemas = JSON.parse(JSON.stringify(FillCommentsComponent.schemasCache[this.wellName]));
            this.loading = false;
            return;
        }

        const configuredSchemas = this.SCHEMA_CONFIG
            .map(config => {
                const match = this.processedSchemas.find((schema: any) =>
                    this.normalizeFieldKey(schema.displayName) === this.normalizeFieldKey(config.displayName)
                );

                return {
                    ...config,
                    apiName: match?.apiName || '',
                    enabled: !!match?.enabled
                };
            })
            .filter(config => config.enabled && !!config.apiName);

        try {
            const loadedSchemas = await Promise.all(
                configuredSchemas.map(async config => {
                    try {
                        const response = await firstValueFrom(
                            this.wellsApiService.getSchema(this.wellName, config.apiName, true)
                        );

                        const records = Array.isArray(response) ? response : [];

                        // Collect all keys from all records for table display (excluding converted_values and unit variants)
                        const allKeysSet = new Set<string>();
                        records.forEach((record: any) => {
                            Object.keys(record).forEach(key => {
                                // Skip converted_values and unit variant keys
                                if (key !== 'converted_values' && 
                                    !key.includes('_in_ft') && 
                                    !key.includes('_in_m') && 
                                    !key.includes('_in_inches') &&
                                    !key.includes('_in_inch')) {
                                    allKeysSet.add(key);
                                }
                            });
                        });
                        const displayKeys = Array.from(allKeysSet).sort();

                        const rows = records.map((record: any) => {
                            const editableValues: any = {};
                            const displayValues: any = {};
                            const resolvedKeys: any = {};

                            config.fields.forEach((field: any) => {
                                const matchedKey = this.findMatchingRecordKey(record, field.aliases);
                                const targetKey = matchedKey || field.aliases[0];

                                resolvedKeys[field.key] = targetKey;
                                editableValues[field.key] = matchedKey ? record[matchedKey] ?? '' : '';
                            });

                            // Store all values for table display
                            displayKeys.forEach((key: string) => {
                                displayValues[key] = record[key] ?? '-';
                            });

                            // ENSURE primary key field is included in displayValues
                            // Find the actual key in the record that matches the primaryKeyField
                            const pkMatchedKey = this.findMatchingRecordKey(record, [config.primaryKeyField]);
                            if (pkMatchedKey) {
                                // Store using both the matched key and the original field name for consistency
                                displayValues[config.primaryKeyField] = record[pkMatchedKey];
                                displayValues[pkMatchedKey] = record[pkMatchedKey];
                            } else {
                                // Fallback: try direct access
                                displayValues[config.primaryKeyField] = record[config.primaryKeyField] ?? '-';
                            }

                            return {
                                originalRecord: { ...record },
                                editableValues,
                                displayValues,
                                resolvedKeys
                            };
                        });

                        return {
                            displayName: config.displayName,
                            apiName: config.apiName,
                            fields: config.fields,
                            displayKeys,
                            rows,
                            error: ''
                        };
                    } catch (error: any) {
                        console.error(`[Fill Comments] Failed to load ${config.displayName}:`, error);
                        return {
                            displayName: config.displayName,
                            apiName: config.apiName,
                            fields: config.fields,
                            displayKeys: [],
                            rows: [],
                            error: 'Unable to load records for this schema.'
                        };
                    }
                })
            );

            this.schemas = loadedSchemas;
            
            // Cache the loaded schemas for this well
            FillCommentsComponent.schemasCache[this.wellName] = JSON.parse(JSON.stringify(loadedSchemas));
            
            this.schemaViewToggle = {};
            loadedSchemas.forEach((_, idx) => {
                this.schemaViewToggle[idx] = false;
            });
            // Set the first schema's apiName for display label resolution
            if (this.schemas.length > 0) {
                this.selectedSchemaApiName = this.schemas[0].apiName;
            }
        } finally {
            this.loading = false;
        }
    }

    public close(): void {
        if (this.saving) {
            return;
        }
        this.closed.emit();
    }

    public toggleRecordsView(schemaIndex: number): void {
        this.schemaViewToggle[schemaIndex] = !this.schemaViewToggle[schemaIndex];
        // Update the selected schema apiName for display label resolution
        if (this.schemas[schemaIndex]) {
            this.selectedSchemaApiName = this.schemas[schemaIndex].apiName;
        }
    }

    public isRecordsVisible(schemaIndex: number): boolean {
        return this.schemaViewToggle[schemaIndex] === true;
    }

    public onFieldChange(schemaIndex: number, rowIndex: number, fieldKey: string, value: any): void {
        const schema = this.schemas[schemaIndex];
        if (!schema || !schema.rows || !schema.rows[rowIndex]) {
            return;
        }

        const fieldConfig = schema.fields.find((field: any) => field.key === fieldKey);
        if (!fieldConfig) {
            return;
        }

        if (fieldConfig.type === 'int') {
            if (value === '' || value === null || value === undefined) {
                schema.rows[rowIndex].editableValues[fieldKey] = null;
                return;
            }

            const parsedValue = Number(value);
            schema.rows[rowIndex].editableValues[fieldKey] = Number.isNaN(parsedValue)
                ? null
                : Math.trunc(parsedValue);
            return;
        }

        schema.rows[rowIndex].editableValues[fieldKey] = value;
    }

    public isAllFieldsFilled(): boolean {
        if (this.schemas.length === 0) {
            return false;
        }

        return this.schemas.every((schema: any) => {
            if (schema.error || !Array.isArray(schema.rows) || schema.rows.length === 0) {
                return true;
            }

            return schema.rows.every((row: any) =>
                schema.fields.every((field: any) => {
                    const value = row.editableValues[field.key];
                    return value !== null && value !== '' && value !== undefined;
                })
            );
        });
    }

    public async confirmAndProceed(): Promise<void> {
        if (this.saving || this.loading) {
            return;
        }

        this.saving = true;

        try {
            const settings = await this.appSettingsService.getSettings();
            const userId = settings?.user?.username || settings?.user?.email || 'unknown';

            // Collect schemas that have changes
            const schemasWithChanges = this.schemas.filter((schema: any) => {
                if (schema.error || !Array.isArray(schema.rows) || schema.rows.length === 0) {
                    return false;
                }

                return schema.rows.some((row: any) =>
                    schema.fields.some((field: any) => this.hasFieldChanged(row, field))
                );
            });

            // Save changes for schemas that have modifications
            if (schemasWithChanges.length > 0) {
                const saveResults = await Promise.allSettled(
                    schemasWithChanges.map(async (schema: any) => {
                        const updatedRecords = schema.rows.map((row: any) => {
                            const updatedRecord = { ...row.originalRecord };

                            schema.fields.forEach((field: any) => {
                                const targetKey = row.resolvedKeys[field.key] || field.aliases[0];
                                updatedRecord[targetKey] = this.normalizeValue(
                                    row.editableValues[field.key],
                                    field.type
                                );
                            });

                            return updatedRecord;
                        });

                        await firstValueFrom(
                            this.httpService.saveEngineerSuggestions(
                                this.wellName,
                                schema.apiName,
                                userId,
                                updatedRecords
                            )
                        );
                    })
                );

                const failedSaves = saveResults.filter(result => result.status === 'rejected');

                if (failedSaves.length > 0) {
                    this.notificationService.error('Some schema records could not be updated. Please try again.');
                    this.saving = false;
                    return;
                }

                this.notificationService.success(`Updated ${schemasWithChanges.length} schema(s) successfully`);
            } else {
                // No changes made, just proceed
                this.notificationService.info('No changes detected. Proceeding with bulk ingest...');
            }

            // Emit the confirmed proceed event to trigger bulk ingest confirmation
            this.confirmedProceed.emit();
        } catch (error: any) {
            console.error('[Fill Comments] Operation failed:', error);
            this.notificationService.error('Failed to process: ' + (error.message || 'Unknown error'));
        } finally {
            this.saving = false;
        }
    }

    public getInputType(fieldType: string): string {
        return fieldType === 'int' ? 'number' : 'text';
    }

    public getDisplayLabel(key: string): string {
        if (!key) return '';
        
        // Get WellView schema key for unit lookup
        const wellViewSchemaKey = this.wellViewMappingService.getWellViewSchemaName(this.selectedSchemaApiName);
        
        // FIRST PRIORITY: Use WellViewMappingService to get the label and unit from mapping.json
        if (wellViewSchemaKey) {
            const label = this.wellViewMappingService.getFieldLabelSync(wellViewSchemaKey, key);
            const unit = this.wellViewMappingService.getFieldUnitSync(wellViewSchemaKey, key);
            
            // If we have both label and unit from mapping, use them
            if (label && unit) {
                return `${label} (${unit})`;
            }
            
            // If we only have label, return it
            if (label) {
                return label;
            }
        }
        
        // SECOND: If the key contains spaces, slashes, or is already a label format
        if (/[\s\/\-]/.test(key)) {
            // Try to get unit from mapping.json by matching the label
            if (wellViewSchemaKey) {
                const unit = this.wellViewMappingService.getFieldUnitSync(wellViewSchemaKey, key);
                if (unit) {
                    return `${key} (${unit})`;
                }
            }
            return key;
        }
        
        // Fallback: format the key
        return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    public getFieldValue(row: any, field: any): string {
        const value = row.editableValues[field.key];
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return String(value);
    }

    public getPrimaryKeyLabel(schemaIndex: number): string {
        if (schemaIndex < 0 || schemaIndex >= this.SCHEMA_CONFIG.length) {
            return '';
        }
        
        const config = this.SCHEMA_CONFIG[schemaIndex];
        // primaryKeyField is already the display label from the API response
        return config.primaryKeyField;
    }

    public getPrimaryKeyValue(schemaIndex: number, row: any): string {
        if (schemaIndex < 0 || schemaIndex >= this.SCHEMA_CONFIG.length || !row) {
            return '-';
        }
        
        const config = this.SCHEMA_CONFIG[schemaIndex];
        
        // First try: direct access using primaryKeyField
        let pkValue = row.displayValues?.[config.primaryKeyField];
        if (pkValue && pkValue !== '-') {
            return pkValue;
        }
        
        // Second try: search displayValues for a key that matches primaryKeyField (case-insensitive)
        if (row.displayValues) {
            const normalizedPkField = this.normalizeFieldKey(config.primaryKeyField);
            const matchedKey = Object.keys(row.displayValues).find(
                key => this.normalizeFieldKey(key) === normalizedPkField
            );
            if (matchedKey) {
                const value = row.displayValues[matchedKey];
                if (value && value !== '-') {
                    return value;
                }
            }
        }
        
        // Third try: check originalRecord (for newly added rows)
        if (row.originalRecord) {
            const matchedKey = this.findMatchingRecordKey(row.originalRecord, [config.primaryKeyField]);
            if (matchedKey) {
                const value = row.originalRecord[matchedKey];
                if (value && value !== '-') {
                    return value;
                }
            }
        }
        
        return '-';
    }

    public getFieldPlaceholder(fieldLabel: string): string {
        return `Enter ${fieldLabel}`;
    }

    public getFieldLabelFromMapping(schemaIndex: number, fieldKey: string, fallbackLabel: string): string {
        if (schemaIndex < 0 || schemaIndex >= this.schemas.length) {
            return fieldKey || fallbackLabel;
        }

        const schema = this.schemas[schemaIndex];
        const wellViewSchemaKey = this.wellViewMappingService.getWellViewSchemaName(schema.apiName);
        
        // Find the field config to get the internal field name for unit lookup
        const fieldConfig = schema.fields?.find((f: any) => this.normalizeFieldKey(f.key) === this.normalizeFieldKey(fieldKey));
        
        // Try to get unit using internal field name
        if (wellViewSchemaKey && fieldConfig?.internalFieldName) {
            const unit = this.wellViewMappingService.getFieldUnitSync(wellViewSchemaKey, fieldConfig.internalFieldName);
            if (unit) {
                return `${fieldKey || fallbackLabel} (${unit})`;
            }
        }
        
        // Fallback: just return the field key as-is
        return fieldKey || fallbackLabel;
    }

    // --- Private helpers ---

    private findMatchingRecordKey(record: any, aliases: string[]): string {
        if (!record || !aliases || aliases.length === 0) {
            return '';
        }

        const recordKeys = Object.keys(record);

        for (const alias of aliases) {
            const aliasKey = this.normalizeFieldKey(alias);
            const matched = recordKeys.find(key => this.normalizeFieldKey(key) === aliasKey);
            if (matched) {
                return matched;
            }
        }

        return '';
    }

    private hasFieldChanged(row: any, field: any): boolean {
        const targetKey = row.resolvedKeys[field.key] || field.aliases[0];
        const originalValue = this.normalizeValue(row.originalRecord?.[targetKey], field.type);
        const currentValue = this.normalizeValue(row.editableValues?.[field.key], field.type);

        if (originalValue === null && currentValue === null) {
            return false;
        }

        return String(originalValue ?? '') !== String(currentValue ?? '');
    }

    private normalizeValue(value: any, fieldType: string): any {
        if (value === '' || value === undefined) {
            return null;
        }

        if (fieldType === 'int') {
            if (value === null) {
                return null;
            }

            const numericValue = typeof value === 'number' ? value : parseInt(value, 10);
            return Number.isNaN(numericValue) ? null : numericValue;
        }

        if (value === null) {
            return null;
        }

        return String(value);
    }

    private normalizeFieldKey(key: string): string {
        return (key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }
}
